/**
 * Incident Count Mission sub-KPI type — HSE cards (DEV-822).
 *
 * Tracks Health, Safety, or Environmental incidents on a project.
 * Renders as a square card above the regular Mission KPI rows.
 *
 * Data source: each MissionDataPoint represents one HSE incident.
 *   - `date` = incident_date (ISO date)
 *   - `value` = severity ordinal (1=near-miss, 2=first-aid, 3=recordable,
 *               4=lost-time, 5=fatality) for legacy compatibility
 *   - `metadata.severity` = severity string (authoritative — see HseSeverity)
 *   - `metadata.title` = incident title
 *   - `metadata.issue_id` = ProjectIssue id (e.g., "INC-0023")
 *   - `metadata.status` = investigation status (open/investigating/etc.)
 *
 * Color logic (asymmetric — the safety record matters):
 *   - period_count = 0                     → green
 *   - all incidents near-miss/first-aid    → yellow
 *   - any incident recordable+             → red
 *
 * Auto-promote (Option A + D from Skip's design):
 *   - Owner picks `display_mode` at creation: 'streak' or 'count'.
 *   - When configured = 'streak' AND period_count > 0, the engine sets
 *     `effective_display_mode = 'count'` and `auto_promoted = true`.
 *     The card UI reads effective_display_mode to decide what to render.
 *   - Auto-promote reverts naturally when the period rolls over with no
 *     new incidents (period_count returns to 0 → no override applies).
 *
 * Streak math watches `incident_date`, NOT issue status. Closing or
 * resolving an investigation does NOT reset the streak — the incident
 * still happened. Only a clean period rollover resets the safety record.
 *
 * See: DEV-822 HSE epic, docs/features/Life4Earth_HSE_Feature_Spec.md
 */

import type {
  ConfigFieldSchema,
  HseCategory,
  HseScoreExtension,
  HseSeverity,
  MissionDataPoint,
  MissionSubKpiType,
  SetupExample,
  SubKpiScore,
  UiHints,
  ValidationError,
} from './types';

/* ------------------------------------------------------------------------- */
/*  Configuration                                                            */
/* ------------------------------------------------------------------------- */

/**
 * Configuration for an Incident Count sub-KPI instance.
 *
 * Stored on `mission_type_config` JSONB on the SubKpiInstance row.
 */
export interface IncidentCountConfig extends Record<string, unknown> {
  /** Display name for the metric (e.g., "Health", "Safety", "Environment"). */
  display_name: string;
  /** Which HSE category this card tracks. Locked when default-provisioned. */
  hse_category: HseCategory;
  /** Owner-chosen default display. May be auto-promoted by the engine. */
  display_mode: 'streak' | 'count';
  /** Reporting period for the count rollup. */
  period: 'week' | 'month' | 'quarter' | 'year';
  /**
   * How long after an incident the card pulses + shows "● New" badge.
   * Defaults to 48h. Configurable per-instance for projects that want
   * the attention pull to last longer (e.g., 7 days for high-stakes work).
   */
  fresh_incident_hours?: number;
  /**
   * Optional ISO date used as the streak baseline when the project has
   * never had an incident. Defaults to the project_start_date if known,
   * otherwise the earliest date in the data set, otherwise asOfDate.
   */
  streak_baseline_date?: string;
}

const DEFAULT_FRESH_INCIDENT_HOURS = 48;

/* ------------------------------------------------------------------------- */
/*  Severity helpers                                                         */
/* ------------------------------------------------------------------------- */

/**
 * DEV-822 Option B: per-category severity color logic. The yellow/red
 * threshold depends on which scale the category uses. Health and Safety
 * use OSHA terms; Environment uses EPA-aligned terms. The boundary is
 * always at the 3rd ordinal (index 2 in the 5-element scale).
 */
const VALID_SEVERITIES = new Set<HseSeverity>([
  // OSHA scale (Health, Safety)
  'near-miss', 'first-aid', 'recordable', 'lost-time', 'fatality',
  // EPA-aligned scale (Environment)
  'minor', 'reportable', 'major', 'catastrophic',
]);

const VALID_CATEGORIES = new Set<HseCategory>([
  'health',
  'safety',
  'environment',
]);

/** Red-band severities by category. */
function isRedSeverity(severity: HseSeverity, category: HseCategory): boolean {
  if (category === 'environment') {
    return severity === 'reportable' || severity === 'major' || severity === 'catastrophic';
  }
  return severity === 'recordable' || severity === 'lost-time' || severity === 'fatality';
}

/** Read severity from a data point. Falls back to value-ordinal for legacy. */
function severityOf(point: MissionDataPoint): HseSeverity {
  const fromMeta = point.metadata?.severity;
  if (typeof fromMeta === 'string' && VALID_SEVERITIES.has(fromMeta as HseSeverity)) {
    return fromMeta as HseSeverity;
  }
  // Legacy: value as severity ordinal (1-5). Defaults to OSHA terms — if
  // an environmental incident has only legacy ordinal data, the engine
  // will still color it correctly via isRedSeverity (which checks category),
  // even though the displayed label may say "first-aid" rather than "minor".
  // New incidents always carry metadata.severity so this fallback is rare.
  switch (Math.round(point.value)) {
    case 1: return 'near-miss';
    case 2: return 'first-aid';
    case 3: return 'recordable';
    case 4: return 'lost-time';
    case 5: return 'fatality';
    default: return 'first-aid'; // Conservative fallback — yellow not green
  }
}

/* ------------------------------------------------------------------------- */
/*  Period boundary calculation                                              */
/* ------------------------------------------------------------------------- */

/**
 * Returns the ISO date string of the start of the current period containing
 * `asOfDate`. Periods are calendar-aligned (Monday-week, ISO-quarter).
 */
export function periodStart(
  period: IncidentCountConfig['period'],
  asOfDate: string,
): string {
  const d = new Date(asOfDate + 'T00:00:00Z');
  switch (period) {
    case 'week': {
      // ISO week: Monday is day 1
      const day = d.getUTCDay() || 7; // Sunday=0 → 7
      d.setUTCDate(d.getUTCDate() - (day - 1));
      break;
    }
    case 'month':
      d.setUTCDate(1);
      break;
    case 'quarter': {
      const m = d.getUTCMonth();
      d.setUTCMonth(m - (m % 3));
      d.setUTCDate(1);
      break;
    }
    case 'year':
      d.setUTCMonth(0);
      d.setUTCDate(1);
      break;
  }
  return d.toISOString().slice(0, 10);
}

/** i18n key for the period label, used by the card foot text. */
function periodLabelKey(period: IncidentCountConfig['period']): string {
  return `kpi:hse.period.this${period.charAt(0).toUpperCase()}${period.slice(1)}`;
}

/* ------------------------------------------------------------------------- */
/*  Engine: scoring                                                          */
/* ------------------------------------------------------------------------- */

function scoreIncidentCount(
  config: IncidentCountConfig,
  data: MissionDataPoint[],
  asOfDate: string,
): SubKpiScore {
  // Sort newest-first by incident date for downstream "most recent" lookups.
  const sorted = [...data].sort((a, b) => (a.date < b.date ? 1 : -1));

  // Period boundary
  const startIso = periodStart(config.period, asOfDate);
  const inPeriod = sorted.filter((p) => p.date >= startIso && p.date <= asOfDate);
  const periodCount = inPeriod.length;

  // Color derivation — category-aware (DEV-822 Option B).
  let score: number;
  if (periodCount === 0) {
    score = 100;
  } else {
    const anyRed = inPeriod.some((p) => isRedSeverity(severityOf(p), config.hse_category));
    score = anyRed ? 0 : 70;
  }

  // Streak — days since most recent incident overall (not just in period)
  const lastIncident = sorted[0] ?? null;
  const streakDays = computeStreakDays(
    lastIncident?.date ?? null,
    config.streak_baseline_date ?? null,
    asOfDate,
  );

  // Auto-promote: configured=streak + count>0 → effective=count
  const autoPromoted =
    config.display_mode === 'streak' && periodCount > 0;
  const effectiveDisplayMode: 'streak' | 'count' = autoPromoted
    ? 'count'
    : config.display_mode;

  // Fresh-incident: most recent incident within fresh_incident_hours
  const freshHours = config.fresh_incident_hours ?? DEFAULT_FRESH_INCIDENT_HOURS;
  const freshIncident = isFresh(lastIncident, asOfDate, freshHours);

  // Most recent incident metadata for foot preview
  const mostRecent = inPeriod[0] ?? lastIncident ?? null;
  const hse: HseScoreExtension = {
    configured_display_mode: config.display_mode,
    effective_display_mode: effectiveDisplayMode,
    auto_promoted: autoPromoted,
    period_count: periodCount,
    period_label_key: periodLabelKey(config.period),
    streak_days: streakDays,
    last_incident_date: lastIncident?.date ?? null,
    fresh_incident: freshIncident,
    most_recent_severity: mostRecent ? severityOf(mostRecent) : undefined,
    most_recent_title: mostRecent
      ? (typeof mostRecent.metadata?.title === 'string'
          ? (mostRecent.metadata.title as string)
          : undefined)
      : undefined,
    most_recent_issue_id: mostRecent
      ? (typeof mostRecent.metadata?.issue_id === 'string'
          ? (mostRecent.metadata.issue_id as string)
          : undefined)
      : undefined,
  };

  const explain =
    periodCount === 0
      ? `Clean — ${streakDays} days incident-free.`
      : `${periodCount} incident${periodCount === 1 ? '' : 's'} in current period; ` +
        `most recent severity: ${hse.most_recent_severity}.`;

  return {
    score_0_100: score,
    // DEV-864: HSE rendering path uses a custom HSERow component, not the
    // standard MissionSubKpiCard progress bar. progress_0_100 is null because
    // there's no meaningful "% of target reached" semantics — the goal is
    // always zero incidents, and the score is category-aware (red severity
    // forces 0, no incidents = 100).
    progress_0_100: null,
    current_value: periodCount,
    target_value: 0, // The goal is always zero incidents
    baseline_value: null,
    explain,
    hse,
  };
}

/** Days between an incident date and asOfDate. Handles never-incident case. */
function computeStreakDays(
  lastIncidentDate: string | null,
  baselineDate: string | null,
  asOfDate: string,
): number {
  const fromIso = lastIncidentDate ?? baselineDate ?? asOfDate;
  const from = Date.parse(fromIso + 'T00:00:00Z');
  const to = Date.parse(asOfDate + 'T00:00:00Z');
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  return Math.max(0, Math.round((to - from) / 86400000));
}

/** True when the most recent incident is within freshHours of asOfDate. */
function isFresh(
  lastIncident: MissionDataPoint | null,
  asOfDate: string,
  freshHours: number,
): boolean {
  if (!lastIncident) return false;
  const from = Date.parse(lastIncident.date + 'T00:00:00Z');
  const to = Date.parse(asOfDate + 'T23:59:59Z');
  if (Number.isNaN(from) || Number.isNaN(to)) return false;
  const hours = (to - from) / 3600000;
  return hours >= 0 && hours <= freshHours;
}

/* ------------------------------------------------------------------------- */
/*  Color band derivation                                                    */
/* ------------------------------------------------------------------------- */

function colorFromIncidentScore(
  score: SubKpiScore,
  _config: IncidentCountConfig,
): 0 | 1 | 2 | 3 {
  if (score.score_0_100 === null) return 0;
  if (score.score_0_100 >= 100) return 3; // green
  if (score.score_0_100 >= 70) return 2;  // yellow
  return 1;                                // red
}

/* ------------------------------------------------------------------------- */
/*  Configuration schema (rendered by the wizard)                            */
/* ------------------------------------------------------------------------- */

const CONFIG_SCHEMA: ConfigFieldSchema[] = [
  {
    key: 'display_name',
    label: 'Display name',
    type: 'string',
    required: true,
    help_text: 'Shown in the card header (e.g., "Health", "Safety", "Environment").',
  },
  {
    key: 'hse_category',
    label: 'HSE category',
    type: 'enum',
    required: true,
    enum_options: [
      { value: 'health', label: 'Health' },
      { value: 'safety', label: 'Safety' },
      { value: 'environment', label: 'Environment' },
    ],
    help_text: 'Determines which card this metric represents on the Performance tab.',
  },
  {
    key: 'display_mode',
    label: 'Default display',
    type: 'enum',
    required: true,
    default_value: 'streak',
    enum_options: [
      {
        value: 'streak',
        label: 'Streak (days incident-free)',
        help: 'Best for community visibility. Auto-promotes to Count when an incident occurs in the current period.',
      },
      {
        value: 'count',
        label: 'Count (incidents this period)',
        help: 'Best for compliance reporting. Always shows the count for the configured period.',
      },
    ],
    help_text: 'The card displays this by default. The engine may auto-promote Streak to Count when an incident occurs.',
  },
  {
    key: 'period',
    label: 'Reporting period',
    type: 'enum',
    required: true,
    default_value: 'quarter',
    enum_options: [
      { value: 'week', label: 'This week' },
      { value: 'month', label: 'This month' },
      { value: 'quarter', label: 'This quarter' },
      { value: 'year', label: 'This year' },
    ],
    help_text: 'Drives the count rollup and when the streak resets.',
  },
  {
    key: 'fresh_incident_hours',
    label: 'Fresh-incident attention window (hours)',
    type: 'number',
    required: false,
    default_value: DEFAULT_FRESH_INCIDENT_HOURS,
    suffix: 'hours',
    validation: { min: 0, max: 720 },
    help_text: 'How long after an incident the card pulses to draw attention. Default 48 hours.',
  },
];

/* ------------------------------------------------------------------------- */
/*  Validation                                                               */
/* ------------------------------------------------------------------------- */

function validateIncidentCountConfig(
  config: IncidentCountConfig,
): ValidationError | null {
  // Per project convention, schema-level required-field checks are owned
  // by the wizard. Here we only validate cross-field / type-specific rules.

  if (
    typeof config.hse_category === 'string' &&
    !VALID_CATEGORIES.has(config.hse_category)
  ) {
    return {
      field: 'hse_category',
      message: 'Must be health, safety, or environment.',
    };
  }

  if (
    typeof config.display_mode === 'string' &&
    config.display_mode !== 'streak' &&
    config.display_mode !== 'count'
  ) {
    return {
      field: 'display_mode',
      message: 'Must be streak or count.',
    };
  }

  if (config.fresh_incident_hours !== undefined) {
    const hrs = config.fresh_incident_hours;
    if (typeof hrs !== 'number' || hrs < 0 || hrs > 720) {
      return {
        field: 'fresh_incident_hours',
        message: 'Fresh-incident window must be between 0 and 720 hours (30 days).',
      };
    }
  }

  return null;
}

/* ------------------------------------------------------------------------- */
/*  Setup examples                                                           */
/* ------------------------------------------------------------------------- */

const SETUP_EXAMPLES: SetupExample[] = [
  {
    title: 'Health — community visibility (default)',
    description:
      'Track health incidents (illness, exposure, exhaustion). Streak counter for community visibility; auto-promotes to Count when an incident occurs.',
    example_config: {
      display_name: 'Health',
      hse_category: 'health',
      display_mode: 'streak',
      period: 'quarter',
    } as IncidentCountConfig,
  },
  {
    title: 'Safety — compliance reporting',
    description:
      'Track injury-causing safety incidents (slips, falls, equipment). Count display for monthly compliance reporting cadence.',
    example_config: {
      display_name: 'Safety',
      hse_category: 'safety',
      display_mode: 'count',
      period: 'month',
    } as IncidentCountConfig,
  },
  {
    title: 'Environment — long horizon',
    description:
      'Track environmental incidents (spills, contamination, habitat damage). Annual streak; severe incidents are rare but high-impact.',
    example_config: {
      display_name: 'Environment',
      hse_category: 'environment',
      display_mode: 'streak',
      period: 'year',
    } as IncidentCountConfig,
  },
];

/* ------------------------------------------------------------------------- */
/*  UI hints                                                                 */
/* ------------------------------------------------------------------------- */

const UI_HINTS: UiHints = {
  default_chart: 'gauge',
  value_format: 'count_with_unit',
  show_target_line: false,
};

/* ------------------------------------------------------------------------- */
/*  Type definition export                                                   */
/* ------------------------------------------------------------------------- */

export const incidentCountType: MissionSubKpiType<IncidentCountConfig> = {
  type_key: 'incident_count',
  display_name: 'HSE Incident Tracker',
  description:
    'Tracks Health, Safety, or Environmental incidents. Renders as a square card above your Mission KPIs.',
  examples: [
    'Days since last health incident',
    'Safety incidents this month',
    'Near-misses this quarter',
  ],
  use_when:
    'You need always-visible safety state on the Performance tab. Compliance reporting. Honest signal even when Mission outcomes look fine.',
  supported_color_modes: ['score'],
  goal_direction: 'decrease', // Lower count is better
  config_schema: CONFIG_SCHEMA,
  validate_config: validateIncidentCountConfig,
  score: scoreIncidentCount,
  color_from_score: colorFromIncidentScore,
  ui_hints: UI_HINTS,
  setup_examples: SETUP_EXAMPLES,
};
